import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Drawer from './Drawer'
import ChaseChips from './ChaseChips'
import { SkeletonBlock } from './Skeleton'
import * as api from '../lib/api'
import { useCanAct } from '../lib/access'
import { CONVERSATION, TEMPLATE_LABEL, deliveredCounts } from '../lib/chase'
import { timeText, whenText, dateText, dayText } from '../lib/when'
import { formatPhoneDisplay } from '../lib/phone'
import DateRangePicker from './DateRangePicker'

/**
 * What we have actually sent one client about one statement.
 *
 * Read like WhatsApp, oldest first — but only our own side of it. We no
 * longer classify a reply from this drawer (or anywhere in the app): a
 * client's message either resolves on its own — a real number given, a
 * promise, a refusal, the statement itself — or it doesn't, and either way
 * there is nothing left here for a person to read and judge. This view
 * exists to answer one question fast: what have we told them, and when.
 */

export default function ActivityDrawer({ request, onClose }) {
  const canAct = useCanAct()
  const [items, setItems] = useState(null)
  const [counts, setCounts] = useState(null)
  const [conversation, setConversation] = useState(request?.conversation ?? null)
  const [error, setError] = useState(null)
  /* Which channel's thread is showing — only meaningful once both have
     actually sent something (see `showChannelTabs` below); a client with
     only one contact never had a choice to make here in the first place. */
  const [channelTab, setChannelTab] = useState(null)
  /* Set by the server only while the row is REDIRECTED: who the next ask goes to. */
  const [redirect, setRedirect] = useState(null)
  const [asking, setAsking] = useState(false)
  const [asked, setAsked] = useState(null)
  /* The "try a different contact" form, shown only while BAD_CONTACT — a
     bounced send has no client message to classify a redirect off of, so
     this is the manual way back in. See `redirectContactManually`. Which
     field(s) actually show is decided by `conversation.badContactChannel`
     below — a dead WhatsApp number asks for a number, a dead email asks
     for an email, both dead asks for both. */
  const [fixName, setFixName] = useState('')
  const [fixNumber, setFixNumber] = useState('')
  const [fixEmail, setFixEmail] = useState('')
  const [fixSubmitting, setFixSubmitting] = useState(false)
  const [fixError, setFixError] = useState(null)
  /* Which number's DoubleTick link is currently being resolved — a phone
     rather than a bool, since the number picker means more than one could
     plausibly be clicked before the first lookup returns. */
  const [dtLoadingFor, setDtLoadingFor] = useState(null)
  const [suggested, setSuggested] = useState(null)
  /* Oldest first, exactly as the server sends it — see `displayNumbers` below. */
  const [numbers, setNumbers] = useState([])
  /* A real chase DoubleTick has neither confirmed nor failed yet — set only
     while one is genuinely stuck mid-flight; null the rest of the time. It
     deliberately never appears in `items`/`counts` (see the backend's own
     comment on why), so without this a message truly in limbo would look
     identical to a client nothing had ever been sent to. */
  const [pendingSend, setPendingSend] = useState(null)
  /*
   * Which number's own slice of the thread is showing. `null` means "whichever
   * is current" and tracks a fresh redirect automatically; an explicit e164
   * pins to that one specifically. Tracked by value, not by position in the
   * list — a new redirect landing mid-session inserts itself at the front, and
   * an index would silently start pointing at a different number than the one
   * actually chosen.
   */
  const [selectedE164, setSelectedE164] = useState(null)

  const [fFrom, setFFrom] = useState('')
  const [fTo, setFTo] = useState('')
  // Bumped only by Clear — DateRangePicker closes its own popover the moment
  // a complete range is picked, so nothing else needs to signal it normally.
  const [filterCloseSignal, setFilterCloseSignal] = useState(0)

  const id = request?._id
  /*
   * A row the collection screen built as a placeholder — a client with no
   * `LedgerRequest` at all, because nothing has ever opened a period for
   * them. `_id` on one of these is a synthetic `ledger-<clientCode>` string,
   * not a real one activityFor could ever resolve — every genuine request
   * has a `periodLabel` (the model requires it), so its absence is the
   * signal, not the id's shape, which this component has no business
   * knowing about.
   */
  const hasRealRequest = Boolean(request?.periodLabel)

  const load = useCallback(async () => {
    if (!id) return
    setError(null)
    if (!hasRealRequest) {
      // Nothing to fetch — there is no request yet for activityFor to find,
      // and hitting it anyway only ever produces a "request not found" that
      // reads as a fault rather than as the ordinary, common case it is.
      setItems([])
      setCounts({ WHATSAPP: 0, EMAIL: 0, CALL: 0, OTHER: 0, total: 0, failed: 0 })
      setConversation(request?.conversation ?? null)
      setRedirect(null)
      setSuggested(null)
      setNumbers([])
      setPendingSend(null)
      return
    }
    try {
      const data = await api.getActivity(id)
      setItems(data.items)
      setCounts(data.counts)
      setConversation(data.conversation)
      setRedirect(data.redirect ?? null)
      setSuggested(data.suggestedTemplate ?? null)
      setNumbers(data.numbers ?? [])
      setPendingSend(data.pendingSend ?? null)
    } catch (e) {
      setError(e.message)
    }
  }, [id, hasRealRequest, canAct])

  useEffect(() => {
    load()
  }, [load])

  /*
   * Opening the drawer is what "reading" means, so mark it read once per open.
   *
   * Fired after the thread is on screen, not folded into load(). The badge on
   * the row behind updates on the list's next refresh — the mutation is on the
   * server, so nothing here has to reconcile it by hand.
   */
  useEffect(() => {
    // Only admins clear the unread badge — an accountant looking doesn't count as read.
    if (!id || !hasRealRequest || !canAct) return
    api.markThreadRead(id).catch(() => {
      /* a failed read-marker is cosmetic — never surface it over the thread */
    })
  }, [id, hasRealRequest])

  /**
   * Ask the person a redirect pointed us at, now.
   *
   * The one send action in this console, and it exists because of what a redirect
   * costs to leave sitting: the counters were reset, so nothing about the row
   * looks urgent, and the new contact has had nothing from us at all.
   *
   * Not automatic on saving the redirect, deliberately. That number was typed a
   * moment ago — sometimes filled in from the message by a guess — and a Save
   * that messages a stranger gives nobody the chance to read it first. Pressing
   * this is that chance, and it is one press.
   *
   * Skipping it costs nothing either: the row is due today, so the scheduled pass
   * sends the same first ask on its next run. This is the fast path, not the only
   * one — which is why the button says what happens if it is ignored.
   */
  const askNow = useCallback(async () => {
    setAsking(true)
    setError(null)
    try {
      await api.sendWhatsApp(id, { templateKey: suggested ?? 'REQUEST_WA_1' })
      setAsked(redirect?.name || redirect?.e164 || 'the new contact')
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setAsking(false)
    }
  }, [id, suggested, redirect, load])

  /**
   * A person has found the number that actually works — by phone, by the
   * ERP, however — and is recording it. Resets the cycle the same way a
   * classified redirect already does; `load()` picks up the new
   * `conversation.state` (REDIRECTED) and the `redirect` block above takes
   * over from there, same as any other redirect.
   */
  const submitContactFix = useCallback(async () => {
    setFixSubmitting(true)
    setFixError(null)
    try {
      await api.redirectContact(id, {
        name: fixName.trim(),
        whatsAppNo: fixNumber.trim() || null,
        email: fixEmail.trim() || null,
      })
      setFixName('')
      setFixNumber('')
      setFixEmail('')
      await load()
    } catch (e) {
      setFixError(e.message)
    } finally {
      setFixSubmitting(false)
    }
  }, [id, fixName, fixNumber, fixEmail, load])

  /**
   * Open this number's chat in DoubleTick's own console, in a new tab.
   *
   * DoubleTick's chat URL needs its own contact id, which nothing lereco
   * stores ever carries — resolved here, on the click, via a fresh lookup.
   * A number DoubleTick has never messaged has no contact yet; that reads as
   * an ordinary "no chat there", not an error.
   */
  const openInDoubleTick = useCallback(async (e164) => {
    setDtLoadingFor(e164)
    setError(null)
    try {
      const { url } = await api.getDoubleTickLink(e164)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      setError(e.message)
    } finally {
      setDtLoadingFor(null)
    }
  }, [])

  // Falls back the same way CollectionRow's own lookup does — a state the
  // backend added and this file hasn't caught up to yet should read as
  // "not contacted" for a moment, not crash the drawer.
  const state = CONVERSATION[conversation?.state ?? 'NOT_STARTED'] ?? CONVERSATION.NOT_STARTED

  /*
   * Every number this request has been chased on, latest first, each with the
   * window of time it was actually the active one — `until` is the moment the
   * *next* number took over, or `null` on the current one, still open-ended.
   *
   * A request with no redirects still gets exactly one entry here, which is
   * why the picker below only renders once there is a real choice — one
   * number showing a dropdown that leads nowhere is worse than no dropdown.
   */
  const displayNumbers = useMemo(() => {
    return [...numbers]
      .map((n, i) => ({ ...n, until: numbers[i + 1]?.from ?? null }))
      .reverse()
  }, [numbers])

  const selectedNumber =
    (selectedE164 && displayNumbers.find((n) => n.e164 === selectedE164)) || displayNumbers[0] || null

  // Narrowed to the selected number's own window — see `displayNumbers` above.
  // No-op whenever there is nothing to narrow: a single-number request (or no
  // items yet) shows everything, same as before this existed.
  const visibleItems =
    items && selectedNumber && displayNumbers.length > 1
      ? items.filter((it) => {
          const at = new Date(it.at).getTime()
          if (selectedNumber.from && at < new Date(selectedNumber.from).getTime()) return false
          if (selectedNumber.until && at >= new Date(selectedNumber.until).getTime()) return false
          return true
        })
      : items

  /*
   * A tab to switch channel only makes sense once both have actually sent
   * something — a client reachable on just one contact never had a choice
   * to begin with, same reasoning as the "Number" picker only appearing
   * once a redirect has actually happened (see `displayNumbers` above).
   */
  const showChannelTabs = Boolean(counts?.WHATSAPP > 0 && counts?.EMAIL > 0)
  const activeChannel = showChannelTabs ? channelTab ?? 'WHATSAPP' : null

  /*
   * What actually shows: our own sent messages only, newest number window
   * first (from `visibleItems` above), narrowed further by date if a range
   * is set and by channel if there is a tab to narrow by. Never a client's
   * own reply and never a system note — this is a record of what we said,
   * not a transcript of the conversation.
   */
  const filteredItems = useMemo(() => {
    if (!visibleItems) return visibleItems
    return visibleItems.filter((it) => {
      if (it.kind !== 'chase' || it.direction !== 'OUT') return false
      if (activeChannel && it.channel !== activeChannel) return false
      if (!fFrom && !fTo) return true
      const at = new Date(it.at).getTime()
      if (fFrom && at < new Date(`${fFrom}T00:00:00`).getTime()) return false
      if (fTo && at > new Date(`${fTo}T23:59:59.999`).getTime()) return false
      return true
    })
  }, [visibleItems, fFrom, fTo, activeChannel])

  const clearFilters = () => {
    setFFrom('')
    setFTo('')
    setFilterCloseSignal((n) => n + 1)
  }

  /* Read like WhatsApp: opens on the latest message, not the oldest. Keyed on
     the thread itself rather than firing once on mount, so a filter change
     or a fresh send (askNow's own reload) also lands back at the bottom. */
  const bottomRef = useRef(null)
  useEffect(() => {
    if (filteredItems?.length) bottomRef.current?.scrollIntoView({ block: 'end' })
  }, [filteredItems])

  return (
    <Drawer
      title={request?.clientName || request?.clientCode}
      subtitle={
        <>
          {hasRealRequest
            ? request.requestedAt
              ? `${request.periodLabel} · first asked ${whenText(request.requestedAt)}`
              : `${request.periodLabel} · never asked`
            : 'No collection period open yet · never contacted'}
          {/* Always shown, not only alongside the picker below — the common
              case is one number and no picker at all, and this was the only
              way anyone could previously tell which number is even open here. */}
          {selectedNumber && (
            <>
              {' '}
              <button
                type="button"
                className="dt-open"
                disabled={dtLoadingFor === selectedNumber.e164}
                onClick={() => openInDoubleTick(selectedNumber.e164)}
                title="Open this number's chat in DoubleTick"
              >
                <svg className="dt-open-ico" viewBox="0 0 24 24" width="11" height="11" aria-hidden="true">
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3"
                  />
                </svg>
                {formatPhoneDisplay(selectedNumber.e164)}
                <span className="dt-open-lbl">DoubleTick</span>
                {dtLoadingFor === selectedNumber.e164 ? '…' : ''}
              </button>
            </>
          )}
        </>
      }
      onClose={onClose}
      summary={
        <>
          <span className={`pill ${state.tone}`}>
            <span className="d" />
            {state.label}
          </span>
          {conversation?.promisedFor && <span className="c-sub">by {dateText(conversation.promisedFor)}</span>}
          {conversation?.disputed && (
            <span className="pill p-bad" title="They disputed a figure — the chase continues; someone should look at it">
              <span className="d" />
              Disputed
            </span>
          )}
          <ChaseChips
            counts={counts ? deliveredCounts(counts, counts.failedByChannel) : deliveredCounts(request?.chaseCounts, request?.failedCounts)}
            size={12}
          />
          <span className="mute" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
            {(counts?.total ?? 0) === 0
              ? 'nothing sent'
              : `${Math.max(0, counts.total - (counts.failed ?? 0))} got through`}
            {counts?.failed > 0 && (
              <span style={{ color: 'var(--alert)' }}> · {counts.failed} more didn't</span>
            )}
          </span>
        </>
      }
      toolbar={
        <div className="lfilter">
          <div className="lfield">
            <span className="lfilter-lbl">Filter</span>
            <div className="lfield-row">
              {/* Staged: passing onApply puts the calendar itself in charge of
                  the in-progress pick, so nothing here reaches fFrom/fTo (the
                  actual applied filter) until Apply inside the popover fires —
                  the in-popover Clear only resets the pick, never the filter. */}
              <DateRangePicker
                from={fFrom}
                to={fTo}
                closeSignal={filterCloseSignal}
                onApply={({ from, to }) => { setFFrom(from); setFTo(to) }}
              />
            </div>
          </div>

          {/* Only when there is an actual choice — a request nobody has ever
              redirected gets exactly one entry in `displayNumbers`, and a
              picker offering one option that does nothing is worse than no
              picker. */}
          {displayNumbers.length > 1 && (
            <label className="numberpick">
              <span className="c-sub">Number</span>
              <select value={selectedNumber?.e164 ?? ''} onChange={(e) => setSelectedE164(e.target.value)}>
                {displayNumbers.map((n, i) => (
                  <option key={n.e164} value={n.e164}>
                    {formatPhoneDisplay(n.e164)}
                    {n.name ? ` — ${n.name}` : ''}
                    {/* Every entry past the first got here by being redirected
                        away from — a request with no redirect never has a second
                        entry at all (see the comment above this picker) — so that
                        is the one honest label for "not current" here, not a bare
                        absence of one. */}
                    {i === 0 ? ' (current)' : ' (redirected)'}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Same "only when there's a real choice" rule as the Number picker
              above — a client we've only ever reached on one channel has
              nothing to switch to. No counts on the labels themselves — the
              chips in the summary bar above already say how many of each,
              and repeating the same number here read as the tabs disagreeing
              with themselves rather than confirming each other. */}
          {showChannelTabs && (
            <div className="seg" role="radiogroup" title="Channel" style={{ marginLeft: 'auto' }}>
              {[
                { value: 'WHATSAPP', label: 'WhatsApp' },
                { value: 'EMAIL', label: 'Email' },
              ].map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={o.value === activeChannel}
                  className={`seg-b${o.value === activeChannel ? ' on' : ''}`}
                  onClick={() => setChannelTab(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
      }
    >
      {error && (
        <div className="note" style={{ borderColor: 'var(--alert)' }}>
          {error}
        </div>
      )}

      {/*
        A bounced contact has nothing that will ever bring it back on its
        own — the scheduler already tried switching to the other channel
        (see `conversationStateFor`'s `badContactChannel`); this is what's
        left once that's also proven dead, or the only channel that ever
        had a contact in the first place. This is the manual way back in: a
        person who has found the working number or address by some other
        means records it here, which resets the cycle exactly like a
        classified redirect would.

        Which channel actually died decides which field(s) show —
        `badContactChannel` is 'WHATSAPP', 'EMAIL', or 'BOTH'. Falls back to
        showing both fields for a request with no channel info yet (an old
        row saved before this field existed), so nobody is ever left with a
        BAD_CONTACT row and no way to fix it at all.
      */}
      {conversation?.state === 'BAD_CONTACT' && (() => {
        const badChannel = conversation?.badContactChannel ?? 'BOTH'
        const showNumber = badChannel === 'WHATSAPP' || badChannel === 'BOTH'
        const showEmail = badChannel === 'EMAIL' || badChannel === 'BOTH'
        const explanation =
          badChannel === 'WHATSAPP'
            ? 'WhatsApp refused our last message to this number.'
            : badChannel === 'EMAIL'
              ? 'Email refused our last message to this address.'
              : 'Both WhatsApp and email have refused our last message.'
        return (
          <div className="gate hold" style={{ marginBottom: 12 }}>
            <span className="gi">!</span>
            <span style={{ display: 'block', whiteSpace: 'normal' }}>
              {CONVERSATION.BAD_CONTACT.label} — {explanation}{' '}
              {canAct ? "If you've found a contact that actually works, enter it below to pick the chase back up." : 'An admin can record a contact that works.'}
              {canAct && (
              <span style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Name (optional)"
                  value={fixName}
                  onChange={(e) => setFixName(e.target.value)}
                  style={{ maxWidth: 160 }}
                />
                {showNumber && (
                  <input
                    type="text"
                    placeholder="Corrected WhatsApp number"
                    value={fixNumber}
                    onChange={(e) => setFixNumber(e.target.value)}
                    style={{ maxWidth: 230 }}
                  />
                )}
                {showEmail && (
                  <input
                    type="email"
                    placeholder="Corrected email address"
                    value={fixEmail}
                    onChange={(e) => setFixEmail(e.target.value)}
                    style={{ maxWidth: 230 }}
                  />
                )}
                <button
                  className="btn btn-sm"
                  onClick={submitContactFix}
                  disabled={fixSubmitting || (!fixNumber.trim() && !fixEmail.trim())}
                >
                  {fixSubmitting ? 'Saving…' : 'Try this contact'}
                </button>
              </span>
              )}
              {fixError && (
                <span style={{ display: 'block', marginTop: 6, color: 'var(--alert)' }}>{fixError}</span>
              )}
            </span>
          </div>
        )
      })()}

      {/* A redirect, acted on but not yet sent. Names the person and the number,
          because that is the thing worth checking before it goes — and says what
          happens if nobody presses it, so leaving it is a choice rather than a
          gamble. */}
      {redirect && !asked && (
        <div className="gate hold" style={{ marginBottom: 12 }}>
          <span className="gi">→</span>
          <span>
            They told us to ask <b>{redirect.name || 'someone else'}</b> on <b>{redirect.e164}</b>. Nothing
            has been sent to them yet — the cycle starts again with the first ask.
            {canAct && (
            <span style={{ display: 'block', marginTop: 8 }}>
              <button className="btn btn-sm" onClick={askNow} disabled={asking}>
                {asking ? 'Sending…' : `Ask ${redirect.name || 'them'} now`}
              </button>
              <span className="c-sub" style={{ marginLeft: 10, whiteSpace: 'normal' }}>
                or leave it — the scheduled run sends the same message today
              </span>
            </span>
            )}
          </span>
        </div>
      )}

      {asked && (
        <div className="gate pass" style={{ marginBottom: 12 }}>
          <span className="gi">✓</span>
          <span>
            First ask sent to <b>{asked}</b>. Future chases go to that number.
          </span>
        </div>
      )}

      {/* A real chase DoubleTick accepted but hasn't confirmed or failed yet.
          Deliberately absent from the thread below and from every count on
          this row — see the backend's own note on why — so this banner is
          the only place "why is this on hold" gets answered while it lasts. */}
      {pendingSend && (
        <div className="gate hold" style={{ marginBottom: 12 }}>
          <span className="gi">…</span>
          <span>
            {TEMPLATE_LABEL[pendingSend.templateKey] ?? pendingSend.templateKey ?? 'A message'} was sent{' '}
            {dayText(pendingSend.attemptedAt)} at {timeText(pendingSend.attemptedAt)}, and DoubleTick hasn't said
            whether it actually went through yet. It won't show as sent, or count as a chase, until it does.
          </span>
        </div>
      )}

      {items === null && !error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SkeletonBlock height={38} style={{ width: '80%', borderRadius: 12 }} />
          <SkeletonBlock height={30} style={{ width: '55%', marginLeft: 'auto', borderRadius: 12 }} />
          <SkeletonBlock height={46} style={{ width: '70%', borderRadius: 12 }} />
        </div>
      )}

      {items?.length === 0 && (
        <div className="note">
          {hasRealRequest ? (
            <>
              Nothing has been sent to this client about their {request.periodLabel} statement, and nothing has
              come back. <b>A silent client here is us, not them.</b>
            </>
          ) : (
            <>No collection period has been opened for this client yet, so nothing has been sent or received.</>
          )}
        </div>
      )}

      {items && items.length > 0 && visibleItems?.length === 0 && (
        <div className="note">Nothing was sent or received on this number.</div>
      )}

      {visibleItems && visibleItems.length > 0 && filteredItems?.length === 0 && (
        <div className="note">
          Nothing matches these filters.{' '}
          <button type="button" className="btn btn-sm btn-ghost" onClick={clearFilters}>
            Clear filters
          </button>
        </div>
      )}

      {filteredItems?.length > 0 && <Thread items={filteredItems} requestId={id} />}
      <div ref={bottomRef} />
    </Drawer>
  )
}

/**
 * The thread, read like WhatsApp: oldest first, our own messages only.
 *
 * The API returns newest-first (what a list wants); a conversation reads the
 * other way round.
 */
function Thread({ items, requestId }) {
  const ordered = [...items].reverse()
  let lastDay = null

  return (
    <div className="wachat">
      {ordered.map((it, i) => {
        const day = dayText(it.at)
        const showDay = day !== lastDay
        lastDay = day
        return (
          <div key={it.id ?? `${it.at}-${i}`}>
            {showDay && <div className="wa-day"><span>{day}</span></div>}
            <Bubble item={it} requestId={requestId} />
          </div>
        )
      })}
    </div>
  )
}

/**
 * Delivery ticks for our own messages — the WhatsApp signal, not a sentence.
 *
 * one grey tick = sent · two grey = delivered · two blue = read · red = failed.
 * Derived from the timestamps the status webhook writes, so it says exactly what
 * the provider attested and no more.
 */
function Ticks({ item }) {
  if (item.failedAt) return <span className="wa-tick fail" title={item.failReason || 'failed'}>!</span>
  const read = Boolean(item.readAt)
  const delivered = Boolean(item.deliveredAt) || read
  return (
    <span className={`wa-tick${read ? ' read' : ''}`} title={read ? 'read' : delivered ? 'delivered' : 'sent'}>
      {delivered ? '✓✓' : '✓'}
    </span>
  )
}

/**
 * A file attached to one of our own messages, as a downloadable row.
 *
 * The download goes through our own backend, not straight to the CDN: the
 * DoubleTick media host answers 403 without the API key and is a different
 * origin, so the browser cannot pull it. `api.downloadMedia` fetches it with our
 * session and saves it.
 */
function FileRow({ requestId, providerMsgId, media }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const name = media.fileName || (media.type === 'IMAGE' ? 'Photo' : 'Document')
  const size = media.sizeBytes ? `${Math.round(media.sizeBytes / 1024)} KB` : null

  const download = async () => {
    if (!providerMsgId) return
    setBusy(true)
    setErr(null)
    try {
      await api.downloadMedia(requestId, providerMsgId, name)
    } catch (e) {
      setErr(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="wa-file out">
      <button className="wa-file-btn" onClick={download} disabled={busy || !providerMsgId} title="Download">
        <span className="wa-file-ico">
          {busy ? <span className="wa-spin" /> : (
            <svg viewBox="0 0 24 24" width="18" height="18"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14"/></svg>
          )}
        </span>
        <span className="wa-file-meta">
          <span className="wa-file-name">{name}</span>
          <span className="wa-file-sub">{size ? size + ' · ' : ''}{busy ? 'downloading…' : 'tap to download'}</span>
        </span>
      </button>
      {err && <div className="wa-file-skip">{err}</div>}
    </div>
  )
}

/** One of our own sent messages, as a bubble. */
function Bubble({ item, requestId }) {
  const media = item.media ?? []
  const body = item.subject ? `Subject: ${item.subject}\n\n${item.body}` : item.body
  const placeholder = !body && media.length === 0 ? (item.note ?? 'Sent a message') : null

  return (
    <div className="wa-row out">
      <div className="wa-bub out">
        {body && <span className="wa-text">{body}</span>}
        {placeholder && <span className="wa-text wa-ph">{placeholder}</span>}
        {media.map((m, k) => (
          <FileRow key={`${item.id}-f${k}`} requestId={requestId} providerMsgId={item.providerMsgId} media={m} />
        ))}
        <span className="wa-meta">
          {item.promisedFor && <span className="wa-by">by {dateText(item.promisedFor)}</span>}
          <span className="wa-time">{timeText(item.at)}</span>
          <Ticks item={item} />
        </span>
      </div>
    </div>
  )
}
